import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import api from '../services/api';

type UserRole = 'JOB_SEEKER' | 'JOB_POSTER' | 'MODERATOR' | 'ADMIN';

export interface AuthUser {
  id: number;
  username: string;
  email: string;
  user_type: UserRole;
  is_approved: boolean;
  first_name?: string;
  last_name?: string;
  career?: string;
  company_name?: string;
  company_website?: string;
  phone_number?: string;
  dni?: string;
  cuit_cuil?: string;
  bio?: string;
  skills?: string[];
}

interface AuthContextValue {
  user: AuthUser | null;
  isAuthenticated: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  loading: boolean;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is already logged in
    const checkAuth = async () => {
      try {
        // First, ensure CSRF token is set
        await api.getCsrfTokenFromServer();
        const userData = await api.getCurrentUser();
        setUser(userData);
      } catch (error) {
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, []);

  const login = async (username: string, password: string) => {
    const userData = await api.login(username, password);
    setUser(userData);
  };

  const logout = async () => {
    try {
      await api.logout();
    } catch (error) {
      if (error instanceof Error && error.message.includes('Authentication credentials were not provided')) {
        // If the session is already gone, still treat as logged out.
      } else {
        throw error;
      }
    } finally {
      setUser(null);
    }
  };

  const refreshUser = async () => {
    try {
      const userData = await api.getCurrentUser();
      setUser(userData);
    } catch (error) {
      setUser(null);
    }
  };

  const value = useMemo(
    () => ({ user, isAuthenticated: Boolean(user), login, logout, refreshUser, loading }),
    [user, loading]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextValue => {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return ctx;
};
